import { useEffect, useState } from 'react';
import {
  suportaAvisos,
  permissaoNegada,
  precisaInstalarNoIphone,
  ligarAvisos,
  situacaoAvisos,
  estaLigadoPara,
} from '@/lib/avisos';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { ehIOS, ehAndroid } from '@/lib/navegador';
import Sheet from '@/components/ui/Sheet';
import FolhaColocarNaTela from '@/components/feature/FolhaColocarNaTela';

type Estado = 'verificando' | 'normal' | 'ligando' | 'ligado';

interface Props {
  eventoId: number;
  /** Nome do evento (entra na frase do aviso). */
  eventoNome?: string;
}

// Botão pequeno "Me avise" (sino) da página do evento. Ao tocar:
//  · permissão liberada → liga na hora;
//  · bloqueada → abre uma folha curta com o passo a passo;
//  · iPhone sem o app → passo a passo de colocar na tela inicial.
export default function BotaoMeAvise({ eventoId, eventoNome }: Props) {
  const [estado, setEstado] = useState<Estado>('verificando');
  const [folhaBloqueado, setFolhaBloqueado] = useState(false);
  const [folhaInstalar, setFolhaInstalar] = useState(false);

  useEffect(() => {
    if (!suportaAvisos()) return;
    let ativo = true;
    situacaoAvisos().then((sit) => {
      if (ativo) setEstado(estaLigadoPara(sit, eventoId) ? 'ligado' : 'normal');
    });
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  async function ligar() {
    if (precisaInstalarNoIphone()) {
      setFolhaInstalar(true);
      return;
    }
    if (permissaoNegada()) {
      setFolhaBloqueado(true);
      return;
    }
    setEstado('ligando');
    try {
      await ligarAvisos({ evento: eventoId, papel: 'comprador' });
      setEstado('ligado');
      mostrarToast('Pronto! A gente te avisa das novidades.', 5000, 'sucesso');
    } catch (e) {
      if (permissaoNegada()) setFolhaBloqueado(true);
      else setEstado('normal');
      mostrarToast(mensagemAmigavel(e, 'Não conseguimos ligar os avisos.'), 6000, 'erro');
    }
  }

  if (!suportaAvisos()) return null;

  const ligado = estado === 'ligado';

  return (
    <>
      <button
        type="button"
        onClick={ligar}
        disabled={estado === 'ligando' || estado === 'verificando'}
        className={`aperta inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[13px] font-semibold transition-colors disabled:opacity-70 ${
          ligado
            ? 'border-success-200 bg-success-50 text-success-700'
            : 'border-background-300 bg-background-50 text-foreground-800 hover:bg-background-100'
        }`}
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i
            className={`${estado === 'ligando' ? 'ri-loader-4-line animate-spin' : ligado ? 'ri-notification-3-fill' : 'ri-notification-3-line'} text-[15px]`}
            aria-hidden
          />
        </span>
        {ligado ? 'Avisos ligados' : 'Me avise'}
      </button>

      <Sheet
        aberto={folhaBloqueado}
        titulo="Como liberar os avisos"
        aoFechar={() => setFolhaBloqueado(false)}
      >
        <p className="text-[15px] leading-relaxed text-foreground-700">
          Os avisos estão bloqueados neste navegador. Para ligar:
        </p>
        <ol className="mt-3 space-y-2 text-[15px] text-foreground-600">
          {ehIOS ? (
            <>
              <li>1. Toque no ⓘ (ou no cadeado) ao lado do endereço.</li>
              <li>2. Abra “Notificações”.</li>
              <li>3. Toque em “Permitir”.</li>
            </>
          ) : ehAndroid ? (
            <>
              <li>1. Toque no cadeado ao lado do endereço.</li>
              <li>2. Abra “Permissões” → “Notificações”.</li>
              <li>3. Toque em “Permitir”.</li>
            </>
          ) : (
            <>
              <li>1. Abra as configurações do navegador.</li>
              <li>2. Vá em “Notificações”.</li>
              <li>3. Autorize o Se Liga Católico.</li>
            </>
          )}
        </ol>
        <button
          type="button"
          onClick={() => setFolhaBloqueado(false)}
          className="aperta mt-5 h-12 w-full cursor-pointer whitespace-nowrap rounded-[14px] bg-primary-500 text-[15px] font-bold text-white"
        >
          Entendi
        </button>
      </Sheet>

      {folhaInstalar && (
        <FolhaColocarNaTela
          aoFechar={() => setFolhaInstalar(false)}
          descricao={
            eventoNome
              ? `No iPhone, os avisos do ${eventoNome} chegam quando o Se Liga está na tela do celular.`
              : 'No iPhone, os avisos chegam quando o Se Liga está na tela do celular.'
          }
        />
      )}
    </>
  );
}